/* START_MODULE_CONTRACT
 * PURPOSE: Admin UI для просмотра и локального переопределения Feature Toggles.
 * SCOPE: FeatureTogglesPage — список флагов, switch override, clear.
 * DEPENDS: M-FE-FEATURE-TOGGLE, M-FE-AUTH
 * LINKS: M-FE-FEATURE-TOGGLE-UI, V-M-FE-FEATURE-TOGGLE-UI, UC-022
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeatureTogglesPage - /admin/feature-toggles
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022 Feature Toggles admin page.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { useFeatureToggle } from '../../context/FeatureToggleContext';
import {
  FLAG_ENABLE_REGISTRATION,
  readFeatureOverrides,
} from '../../features/featureToggle/featureToggleStorage';

const FLAG_META = [
  {
    name: FLAG_ENABLE_REGISTRATION,
    label: 'Регистрация пользователей',
    description: 'Разрешает публичные endpoints и страницу /register.',
  },
];

/* START_METHOD_FeatureTogglesPage */
/* START_CONTRACT: FeatureTogglesPage
 * PURPOSE: Показать глобальные/эффективные флаги и дать ADMIN локальные оверрайды.
 * INPUTS: нет
 * OUTPUTS: { JSX.Element }
 * SIDE_EFFECTS: setOverride / clearOverride via context
 * LINKS: V-M-FE-FEATURE-TOGGLE-UI
 * END_CONTRACT: FeatureTogglesPage
 */
export default function FeatureTogglesPage() {
  const { loading, baseFlags, canOverride, isEnabled, setOverride, clearOverride } = useFeatureToggle();
  const overrides = canOverride ? readFeatureOverrides() : {};

  if (!canOverride) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Управление Feature Toggles доступно только администраторам.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  /* START_METHOD_handleToggle */
  /* START_CONTRACT: handleToggle
   * PURPOSE: Включить/выключить локальный оверрайд флага.
   * INPUTS: { flagName: string, checked: boolean }
   * OUTPUTS: { void }
   * SIDE_EFFECTS: setOverride; log BLOCK_TOGGLE_OVERRIDE
   * LINKS: V-M-FE-FEATURE-TOGGLE-UI
   * END_CONTRACT: handleToggle
   */
  const handleToggle = (flagName, checked) => {
    // START_BLOCK_TOGGLE_OVERRIDE
    console.info('[FeatureTogglesPage][handleToggle][BLOCK_TOGGLE_OVERRIDE]', { flagName, checked });
    // END_BLOCK_TOGGLE_OVERRIDE
    setOverride(flagName, checked);
  };
  /* END_METHOD_handleToggle */

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Feature Toggles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Глобальные значения задаются переменной окружения FEATURE_TOGGLE. Локальные оверрайды
        хранятся в браузере и действуют только для вашей сессии администратора.
      </Typography>

      <Stack spacing={2}>
        {FLAG_META.map((flag) => {
          const globalValue = Boolean(baseFlags[flag.name]);
          const hasOverride = Object.prototype.hasOwnProperty.call(overrides, flag.name);
          const effective = isEnabled(flag.name);

          return (
            <Paper key={flag.name} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {flag.label}
                  </Typography>
                  <Chip size="small" label={flag.name} variant="outlined" />
                  {hasOverride ? (
                    <Chip size="small" color="warning" label="override" />
                  ) : (
                    <Chip size="small" label="global" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {flag.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Глобально: {globalValue ? 'вкл' : 'выкл'} · Эффективно: {effective ? 'вкл' : 'выкл'}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={effective}
                      onChange={(e) => handleToggle(flag.name, e.target.checked)}
                      inputProps={{ 'aria-label': flag.label }}
                    />
                  }
                  label="Локальный оверрайд (для меня)"
                />
                {hasOverride && (
                  <Box>
                    <Button size="small" onClick={() => clearOverride(flag.name)}>
                      Сбросить оверрайд
                    </Button>
                  </Box>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
/* END_METHOD_FeatureTogglesPage */
